import React, { useContext, useMemo, useState, useEffect, useRef } from "react";

import {
  AuthContext,
  JobsContext,
  MatchContext,
  SearchContext,
  FilterPanelProvider,
} from "context/providers";

import SearchFilters from "components/SearchFilters";
import {
  ExpandCollapseAllControl,
  SearchSortByDropdown,
} from "components/SearchFilters/components";
import ApplicationsTabs from "./components/ApplicationsTabs";
import UserCardList from "components/UserCardList";

import { useLocation } from "react-router-dom-v5-compat";
import IdealFitFilter from "components/SearchFilters/idealFitFilters";
import { TABS } from "context/constants/common";
import { ChevronRight, LoaderCircle, X } from "lucide-react";

import CustomToggle from "components/base/Toggle";
import { Button } from "components/ui/button";
import { getFilteredUserIDs } from "utils/helpers/search";
import { debounce, isEqual } from "lodash";
import SavedSearches from "components/SavedSearches";
import { formatAISkillSearchString } from "utils/helpers/search";
import { cn } from "utils";
import "./index.css";
import { USER_LIST_VIEWS } from "lookup";
import ScrollToTopButton from "components/ScrollToTopButton";
import {
  getUserListPreferences,
  normalizeUserListViewType,
} from "utils/helpers/userListPreferences";

const CustomSearchBox = ({
  onChange,
  onClear,
  value,
  className,
  showLoadingIndicator,
  placeholder = "Search...",
  id,
}) => {
  return (
    <div id={id} className={cn("ais-SearchBox w-full", className)}>
      <div className="ais-SearchBox-form">
        <input
          type="search"
          placeholder={placeholder}
          className="ais-SearchBox-input"
          onChange={onChange}
          value={value}
        />
        {showLoadingIndicator && (
          <span className="ais-SearchBox-loadingIndicator !right-0.5">
            <LoaderCircle
              strokeWidth={1}
              size={20}
              className="animate-spin opacity-50"
            />
          </span>
        )}
        {!showLoadingIndicator && value && (
          <button
            type="reset"
            title="Clear the search query"
            className="ais-SearchBox-reset !right-0.5 w-[18px]"
            onClick={onClear}
          >
            <X color="#484c7a" />
          </button>
        )}
      </div>
    </div>
  );
};

const durationLength = 300;
const timeoutBuffer = 50;
const widthTransitionClasses = cn(
  "transition-width ease-in-out",
  `duration-${durationLength}`
);

// Wait this long after the last keystroke before running the search, so it only
// fires once the user has clearly stopped typing rather than on brief pauses.
const BASIC_SEARCH_DEBOUNCE_MS = 1000;

// Self-contained search input. Keeping the controlled value in its own component
// means a keystroke only re-renders this input, not the whole Applications tree
// (filters, results, saved searches). The actual search is debounced before it
// touches the shared search state.
const BasicSearchInput = ({ isJobPage }) => {
  const { searchState, setSearchState } = useContext(SearchContext);

  const [basicInputSearch, setBasicInputSearch] = useState(
    () => searchState.customSearchQuery || ""
  );

  // Keep the latest committer in a ref so the debounced function stays stable
  // (created once) while always using the current searchState/setSearchState.
  const commitSearchRef = useRef();
  commitSearchRef.current = (value) => {
    setSearchState({
      ...searchState,
      customSearchQuery: value,
    });
  };

  const debouncedCommitSearch = useMemo(
    () =>
      debounce((value) => {
        commitSearchRef.current(value);
      }, BASIC_SEARCH_DEBOUNCE_MS),
    []
  );

  // Cancel any pending search when the input unmounts.
  useEffect(
    () => () => debouncedCommitSearch.cancel(),
    [debouncedCommitSearch]
  );

  useEffect(() => {
    if (!isJobPage) {
      setBasicInputSearch(
        formatAISkillSearchString(searchState.skills?.required)
      );
    }
  }, [isJobPage, searchState.skills]);

  useEffect(() => {
    setBasicInputSearch((prev) => {
      if (!isEqual(searchState.customSearchQuery, prev)) {
        return searchState.customSearchQuery || "";
      }
      return prev;
    });
  }, [searchState.customSearchQuery]);

  const handleBasicInputChange = ({ target }) => {
    setBasicInputSearch(target.value);
    debouncedCommitSearch(target.value);
  };

  const handleClear = () => {
    // Drop any queued search so a stale term doesn't land after clearing.
    debouncedCommitSearch.cancel();
    setSearchState({
      ...searchState,
      customSearchQuery: "",
    });
  };

  return (
    <div className="flex flex-col gap-1">
      <CustomSearchBox
        id="basic-search"
        onChange={handleBasicInputChange}
        onClear={handleClear}
        value={basicInputSearch}
        placeholder={
          searchState.basicSearch ? "Name, email, username" : "Search..."
        }
      />
    </div>
  );
};

// https://stackoverflow.com/a/65050864/2104976
// Each widget is responsibile for its own state
// We cannot set the default value for job title
// if the refinement list does not exist
// But we don't want to show the refinement list for job title
// So we are creating this "virtual" refinement list

const Applications = ({ user }) => {
  const { jobOpp } = useContext(JobsContext);
  const {
    searchState,
    setSearchState,
    clearSearchState,
    setHideUsersFlags,
    reRankHitsForJob,
    handleReRankHitsForJob,
  } = useContext(SearchContext);

  const { matches } = useContext(MatchContext);

  const { user: loggedInUser } = useContext(AuthContext);

  const isJobPage = useMemo(() => !!jobOpp.id, [jobOpp]);
  const location = useLocation();
  const params = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );
  const currentTab = params.get("tab");
  const isSearchResultsTab = !currentTab || currentTab === TABS.SEARCHRESULTS;
  const tabSupportsFilters = isSearchResultsTab || currentTab === TABS.IDEALFIT;

  const userListPreferences = getUserListPreferences(loggedInUser);
  const currentViewType = normalizeUserListViewType(
    params.get("viewType") || userListPreferences.viewType
  );

  const isMatchAgentViewAndCanBeAccessed =
    userListPreferences.careerTrackAnalysisEnabled &&
    currentViewType === USER_LIST_VIEWS.MATCH_AGENT &&
    loggedInUser.canAccessMatchAgent;

  const shouldBeOpenSideBar =
    !isMatchAgentViewAndCanBeAccessed || !jobOpp?.id || searchState.basicSearch;
  const getInitialSidebarState = () => {
    const storedState = sessionStorage.getItem("filterSidebarExpanded");
    if (storedState !== null) {
      return storedState === "true";
    }

    const isSearchResultsTab = !currentTab || currentTab === TABS.SEARCHRESULTS;
    if (isSearchResultsTab) {
      return true; // Always start expanded for search results tab
    }

    return tabSupportsFilters;
  };

  const [showLeftSidebar, setShowLeftSidebar] = useState(
    getInitialSidebarState()
  );
  const [showFilters, setShowFilters] = useState(getInitialSidebarState());

  useEffect(() => {
    if (!isJobPage || searchState.basicSearch) {
      setShowLeftSidebar(true);
      setShowFilters(true);
    }
  }, [isJobPage, searchState.basicSearch]);

  useEffect(() => {
    if (!isJobPage) {
      return;
    }

    if (isSearchResultsTab) {
      const storedState = sessionStorage.getItem("filterSidebarExpanded");

      if (storedState !== null) {
        const userPreference = storedState === "true";
        setShowLeftSidebar(userPreference);
        setShowFilters(userPreference);
      } else {
        setShowLeftSidebar(true);
        setShowFilters(true);
      }
      return;
    }

    setShowLeftSidebar(() => shouldBeOpenSideBar && tabSupportsFilters);

    const timeoutAmount = tabSupportsFilters
      ? 0
      : durationLength + timeoutBuffer;
    const timeoutId = setTimeout(() => {
      setShowFilters(() => {
        if (tabSupportsFilters) {
          return true;
        }
        return false;
      });
    }, timeoutAmount);

    return () => clearTimeout(timeoutId);
  }, [tabSupportsFilters, shouldBeOpenSideBar, isSearchResultsTab, isJobPage]);

  return (
    <div
      className={cn("w-full flex flex-column", {
        "!border-t-2": isJobPage,
      })}
    >
      <div
        className={cn(
          widthTransitionClasses,
          "relative border-r-2 min-h-[calc(100vh-64px)]",
          {
            "w-1/4": showLeftSidebar,
            "w-0": !showLeftSidebar,
          }
        )}
      >
        {/* Toggle button */}
        {tabSupportsFilters && (
          <Button
            className={cn(
              "absolute -top-10 left-0 border-2 rounded-none rounded-tr-md z-50"
            )}
            onClick={() => {
              const newState = !showLeftSidebar;
              setShowLeftSidebar(newState);
              setShowFilters(newState);
              sessionStorage.setItem(
                "filterSidebarExpanded",
                newState.toString()
              );
            }}
            variant="ghost"
            size="icon"
          >
            <ChevronRight
              className={cn("transition-transform", {
                "rotate-180": showLeftSidebar,
                "rotate-0": !showLeftSidebar,
              })}
            />
          </Button>
        )}
        <div
          className={cn(
            "sticky top-0 z-10 flex h-[calc(100vh-64px)] min-h-0 flex-col overflow-hidden pt-6",
            {
              hidden: !showFilters,
            }
          )}
        >
          <div className="flex min-h-0 min-w-[25vw] flex-1 flex-col">
            <div
              className={cn({
                hidden: currentTab !== TABS.IDEALFIT,
              })}
            >
              <IdealFitFilter />
            </div>
            <div
              className={cn("flex min-h-0 flex-1 flex-col", {
                hidden: currentTab === TABS.IDEALFIT,
              })}
            >
              <FilterPanelProvider>
                <div className="applications-filter-sidebar flex min-h-0 flex-1 flex-col gap-6 px-6">
                  <div className="flex shrink-0 flex-wrap items-baseline justify-between gap-2">
                    <h2 className="text-slate-800 text-xl font-bold">
                      Filter Search
                    </h2>
                    <CustomToggle
                      leftLabel="ADVANCED"
                      rightLabel="BASIC"
                      checked={searchState.basicSearch}
                      className="text-sm font-bold text-slate-500"
                      onChange={(basicSearchOn) => {
                        // Storing as string since getting error cannot delete Cannot delete property 'x' of #<Object>
                        // when destructuring as an object.
                        const previousAdvanceSearchStateStr =
                          JSON.stringify(searchState);

                        let finalState = {};

                        if (basicSearchOn) {
                          finalState = {
                            ...searchState,
                            previousAdvanceSearchStateStr,
                            customSearchQuery: "",
                            configProps: {
                              ...searchState.configProps,
                              facetsExcludes: { objectID: [] },
                            },
                          };

                          if (jobOpp?.id) {
                            handleReRankHitsForJob({
                              id: "",
                              previousValue: { ...reRankHitsForJob },
                            });
                          }

                          setHideUsersFlags({
                            hideSkipped: false,
                            hideRejected: false,
                            hideMatchedAndApplied: false,
                          });
                        } else {
                          const restoredState = JSON.parse(
                            searchState.previousAdvanceSearchStateStr
                          );

                          setHideUsersFlags({
                            hideSkipped: true,
                            hideRejected: true,
                            hideMatchedAndApplied: true,
                          });

                          finalState = {
                            ...searchState,
                            customSearchQuery: "",
                            ...restoredState,
                            previousAdvanceSearchStateStr: "{}",
                            configProps: {
                              ...(restoredState.configProps ?? {}),
                              facetsExcludes: {
                                objectID: getFilteredUserIDs(
                                  matches,
                                  true,
                                  true,
                                  true
                                ),
                              },
                            },
                          };

                          if (
                            jobOpp?.id &&
                            userListPreferences.careerTrackAnalysisEnabled
                          ) {
                            handleReRankHitsForJob({
                              ...reRankHitsForJob?.previousValue,
                              previousValue: undefined,
                            });
                          }
                        }

                        setSearchState({
                          ...finalState,
                          basicSearch: basicSearchOn,
                        });
                      }}
                    />
                  </div>

                  {!searchState.basicSearch && (
                    <div className="flex shrink-0 flex-col gap-6">
                      <SearchSortByDropdown
                        indexName={process.env.REACT_APP_OS_USERS_INDEX_NAME}
                      />

                      <div className="flex items-center gap-6">
                        <button
                          type="button"
                          className="filter-sidebar-btn"
                          onClick={() => {
                            clearSearchState({
                              dontClearSearchAgentSkills: true,
                              searchState,
                            });
                          }}
                        >
                          Clear Filters
                        </button>
                        <ExpandCollapseAllControl />
                      </div>

                      {!isJobPage && <SavedSearches />}
                    </div>
                  )}

                  {searchState.basicSearch ? (
                    <BasicSearchInput isJobPage={isJobPage} />
                  ) : null}

                  {!searchState.basicSearch && (
                    <div className="flex min-h-0 flex-1 flex-col">
                      <div className="filter-sidebar-panels customScrollBar -mx-6 min-h-0 flex-1 overflow-y-auto overflow-x-hidden pb-16">
                        <SearchFilters
                          indexName={process.env.REACT_APP_OS_USERS_INDEX_NAME}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </FilterPanelProvider>
            </div>
          </div>
        </div>
      </div>
      <div className={cn(widthTransitionClasses, "min-w-0 flex-1")}>
        {isJobPage ? (
          <ApplicationsTabs />
        ) : (
          <UserCardList collectionKey="hits" pagination stats />
        )}

        <ScrollToTopButton />
      </div>
    </div>
  );
};

export default Applications;
